<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import dayjs, { Dayjs } from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

import ErrorModal from '@/components/common/ErrorModal.vue'
import PageHeader from '@/components/common/PageHeader.vue'
import SparkBreadcrumb from '@/components/generic/SparkBreadcrumb.vue'
import { ROUTE_NAMES } from '@/router/route-names'
import { useMainStore } from '@/stores'
import { useGasTransitStore } from '@/stores/gasTransit'
import type { components } from '#/types/capi-generated'

import { generateId } from './utils'

type GasTransitUnit = components['schemas']['CAPIGasTransitCostsUnitEnum']
type GasTransitType = components['schemas']['CAPIGasTransitTypeEnum']
type GasTransitProduct = components['schemas']['CAPIGasTransitProductEnum']

const unitOptions: { text: string; value: GasTransitUnit }[] = [
  { text: '€/MWh', value: 'eur-per-mwh' },
  { text: '$/MMBtu', value: 'usd-per-mmbtu' },
  { text: 'p/therm', value: 'p-per-therm' },
]

const typeOptions: { text: string; value: GasTransitType | '' }[] = [
  { text: 'All', value: '' },
  { text: 'Firm', value: 'firm' },
  { text: 'Interruptible', value: 'interruptible' },
]

const productOptions: { text: string; value: GasTransitProduct | '' }[] = [
  { text: 'All', value: '' },
  { text: 'Annual', value: 'annual' },
  { text: 'Quarterly', value: 'quarterly' },
  { text: 'Monthly', value: 'monthly' },
  { text: 'Daily', value: 'daily' },
]

const priceReleasesOptions = [
  { text: 'Latest', value: 'latest' },
  { text: 'Date Range', value: 'date-range' },
]

const startDate = ref<Dayjs>(dayjs().subtract(7, 'day'))
const endDate = ref<Dayjs>(dayjs())

const state = reactive<{
  origin: string
  destination: string
  unit: GasTransitUnit
  type: GasTransitType | ''
  product: GasTransitProduct | ''
  priceReleasesFilter: string
  submitting: boolean
  errors: string[]
  showErrorModal: boolean
}>({
  origin: '',
  destination: '',
  unit: 'eur-per-mwh',
  type: '',
  product: '',
  priceReleasesFilter: 'latest',
  submitting: false,
  errors: [],
  showErrorModal: false,
})

const gasTransitStore = useGasTransitStore()
const mainStore = useMainStore()

const ALL_OPTION = { label: 'All', value: '' }

const originOptions = computed(() => [
  ALL_OPTION,
  ...gasTransitStore.origins.map((o) => ({ label: o, value: o })),
])

const destinationOptions = computed(() => {
  const dests = state.origin
    ? gasTransitStore.destinationsFor(state.origin)
    : gasTransitStore.destinations
  return [ALL_OPTION, ...dests.map((d) => ({ label: d, value: d }))]
})

watch(
  () => state.origin,
  () => {
    // Reset destination to "All" if it's no longer valid for the chosen origin.
    if (!state.origin || !state.destination) return
    if (!gasTransitStore.destinationsFor(state.origin).includes(state.destination)) {
      state.destination = ''
    }
  },
)

const isDateRangeValid = computed(() => {
  if (state.priceReleasesFilter !== 'date-range') return true
  const start = startDate.value.startOf('day')
  const end = endDate.value.endOf('day')
  if (end.isBefore(start)) return false
  return end.diff(start, 'year', true) <= 1
})

async function loadReferenceData() {
  try {
    // Origin and Destination default to "All" (empty); just load the
    // reference data to populate the dropdown options.
    await gasTransitStore.fetchReferenceData()
  } catch (err: unknown) {
    state.errors.push(
      err instanceof Error ? err.message : 'Failed to load reference data',
    )
    state.showErrorModal = true
  }
}

onMounted(loadReferenceData)

const MAX_CELLS_PER_OPERATION = 1000000

async function loadData() {
  state.submitting = true
  state.errors = []

  try {
    const isLatest = state.priceReleasesFilter === 'latest'

    const { columns, rows } = await gasTransitStore.fetchCosts(state.unit, {
      origin: state.origin || undefined,
      destination: state.destination || undefined,
      type: state.type || undefined,
      product: state.product || undefined,
      ...(isLatest
        ? { limit: 1 }
        : {
            start: startDate.value.format('YYYY-MM-DD'),
            end: endDate.value.format('YYYY-MM-DD'),
          }),
    })

    if (columns.length > 0 && rows.length > 0) {
      await pasteTableOntoExcel(columns, rows)
    } else {
      state.errors.push('No transit cost data was returned for the selected filters.')
    }
  } catch (err: unknown) {
    if (err instanceof Error) {
      state.errors.push(err.message)
    } else {
      state.errors.push('An unknown error occurred')
    }
  } finally {
    if (state.errors.length > 0) {
      state.showErrorModal = true
    }
    state.submitting = false
  }
}

async function pasteTableOntoExcel(columns: string[], rows: string[][]) {
  try {
    if (!window.Excel) {
      console.warn('Excel is not loaded')
      return
    }

    const COLUMNS_COUNT = columns.length
    const ROWS_COUNT = rows.length
    const totalCells = ROWS_COUNT * COLUMNS_COUNT

    if (totalCells > MAX_CELLS_PER_OPERATION) {
      console.warn(`Large dataset detected: ${totalCells} cells. Consider splitting into smaller batches.`)
    }

    await window.Excel.run(async (context) => {
      const app = context.workbook.application
      app.suspendScreenUpdatingUntilNextSync()
      app.suspendApiCalculationUntilNextSync()

      const workbook = context.workbook
      const sheet = workbook.worksheets.getActiveWorksheet()
      const activeRange = workbook.getSelectedRange()
      const firstCell = activeRange.getCell(0, 0)

      sheet.load('tables')
      activeRange.load('address')
      await context.sync()

      const existingTableCollection = sheet.tables
      const existingTables = existingTableCollection.items

      const tableDataRange = firstCell.getAbsoluteResizedRange(
        ROWS_COUNT + 1,
        COLUMNS_COUNT
      )

      if (existingTables && existingTables.length > 0) {
        const tablesToDelete: string[] = []
        const tableIntersections: { table: Excel.Table; intersection: Excel.Range }[] = []

        for (const existingTable of existingTables) {
          const range = existingTable.getRange()
          const intersection = range.getIntersectionOrNullObject(tableDataRange)
          intersection.load('address')
          tableIntersections.push({ table: existingTable, intersection })
        }

        await context.sync()

        for (const { table, intersection } of tableIntersections) {
          if (!intersection.isNullObject) {
            tablesToDelete.push(table.name)
          }
        }

        for (const tableName of tablesToDelete) {
          try {
            sheet.tables.getItem(tableName).delete()
          } catch (err) {
            state.errors.push(`Error deleting table ${tableName}: ${JSON.stringify(err)}`)
          }
        }

        if (tablesToDelete.length > 0) {
          await context.sync()
        }
      }

      const allTableData = [columns, ...rows]
      tableDataRange.values = allTableData

      const TABLE_NAME = 'GasTransitTable' + generateId()
      const dataTable = sheet.tables.add(tableDataRange, true)
      dataTable.name = TABLE_NAME

      const headerRange = dataTable.getHeaderRowRange()
      const dataRange = dataTable.getDataBodyRange()

      dataTable.style = 'TableStyleLight1'

      headerRange.format.fill.color = '#2563eb'
      const headerFont = headerRange.format.font
      headerFont.color = '#FFFFFF'
      headerFont.size = 12
      headerFont.name = 'Mont'
      headerFont.bold = true

      dataRange.format.fill.color = '#FFFFFF'
      const dataFont = dataRange.format.font
      dataFont.color = '#000000'
      dataFont.name = 'Roboto'
      dataFont.bold = false

      if (Office.context.requirements.isSetSupported('ExcelApi', '1.2')) {
        const usedRange = sheet.getUsedRange()
        const usedRangeFormat = usedRange.format
        usedRangeFormat.autofitColumns()
        usedRangeFormat.autofitRows()
      }

      sheet.activate()
      await context.sync()
    })
  } catch (err: any) {
    console.error('Error in pasteTableOntoExcel:', err)
    state.errors.push(err.message)
  }
}

const router = useRouter()

function handleBreadcrumbInput(value: string) {
  router.push({ name: value })
}
</script>

<template>
  <Loader :modelValue="mainStore.isLoading">
    <div class="gas-transit-page p-5 space-y-10">
      <section class="space-y-6">
        <PageHeader title="Get Gas Transit Data" />
        <SparkBreadcrumb
          :config="[
            { name: 'Products', icon: 'home-filled', value: ROUTE_NAMES.HOME },
            {
              name: 'Gas Transit',
              icon: 'gasTransit',
              styleClasses: 'bg-gas text-white'
            }
          ]"
          @input="handleBreadcrumbInput"
        />
      </section>

      <a-form layout="vertical">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <a-form-item :colon="false" label="Origin" class="!mb-1">
              <a-select
                v-model:value="state.origin"
                size="small"
                class="w-full"
                placeholder="Select an origin"
                :options="originOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>
          </div>
          <div>
            <a-form-item :colon="false" label="Destination" class="!mb-1">
              <a-select
                v-model:value="state.destination"
                size="small"
                class="w-full"
                placeholder="Select a destination"
                :options="destinationOptions"
                show-search
                option-filter-prop="label"
              />
            </a-form-item>
          </div>
        </div>

        <a-form-item :colon="false" label="Price Unit">
          <a-radio-group
            v-model:value="state.unit"
            button-style="solid"
            size="small"
          >
            <a-radio-button
              v-for="opt in unitOptions"
              :value="opt.value"
              :key="opt.value"
            >{{ opt.text }}</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item :colon="false" label="Type">
          <a-radio-group
            v-model:value="state.type"
            button-style="solid"
            size="small"
          >
            <a-radio-button
              v-for="opt in typeOptions"
              :value="opt.value"
              :key="opt.value"
            >{{ opt.text }}</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item :colon="false" label="Product">
          <a-radio-group
            v-model:value="state.product"
            button-style="solid"
            size="small"
          >
            <a-radio-button
              v-for="opt in productOptions"
              :value="opt.value"
              :key="opt.value"
            >{{ opt.text }}</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <a-form-item :colon="false" label="Releases">
          <a-radio-group
            class="w-full"
            v-model:value="state.priceReleasesFilter"
            size="small"
          >
            <a-radio-button
              v-for="opt in priceReleasesOptions"
              :value="opt.value"
              :key="opt.value"
            >{{ opt.text }}</a-radio-button>
          </a-radio-group>
        </a-form-item>

        <div v-if="state.priceReleasesFilter === 'date-range'">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <a-form-item :colon="false" label="Start Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="startDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
            <div>
              <a-form-item :colon="false" label="End Date" class="!mb-1">
                <a-date-picker
                  class="w-full"
                  v-model:value="endDate"
                  size="small"
                  format="YYYY-MM-DD"
                />
              </a-form-item>
            </div>
          </div>
          <p class="text-sm text-amber-600 flex items-center gap-1 mb-4">
            <InfoCircleOutlined />
            <span>Maximum date range is 1 year</span>
          </p>
        </div>

        <div class="mb-6 rounded-lg border border-gas-100 bg-gas-50 p-4">
          <p class="flex items-start gap-2 text-base text-gray-700">
            <InfoCircleOutlined class="mt-1 shrink-0 text-gas-500" />
            <span>
              Without filters applied, data is limited to approximately 1
              month due to data filesize restrictions. Apply filters to Type,
              Product, and Origin/Destination to retrieve up to 1 year of
              historical data.
            </span>
          </p>
        </div>

        <div>
          <SparkButton
            class="w-full"
            size="lg"
            color="gas"
            :disabled="!isDateRangeValid"
            :loading="state.submitting"
            @click="loadData"
          >Insert data into selected cell</SparkButton>
        </div>
      </a-form>

      <ErrorModal
        v-model:visible="state.showErrorModal"
        title="Some data could not be retrieved"
        description="The following issues were encountered during data retrieval."
        :errors="state.errors"
      />
    </div>
  </Loader>
</template>

<style lang="scss">
.gas-transit-page {
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: #2563eb !important;
    border-color: #2563eb !important;

    &:hover {
      background: #1D58D8 !important;
      border-color: #1D58D8 !important;
    }

    &::before {
      background-color: #2563eb !important;
    }
  }
}
</style>
